import type { ResolvedContactCard } from '@buxus/shared'
import { formatPhoneDisplay, phoneTelHref } from '@buxus/shared'
import { Link } from 'react-router-dom'
import type { ScreenInteractionContext } from '../platform/analytics/interactionAnalytics'
import { trackEmailPressed, trackPhonePressed, trackUrlPressed } from '../platform/analytics/interactionAnalytics'
import { OperatorInlineImage } from '../OperatorInlineImage'

type Props = {
  contact: ResolvedContactCard
  operatorId: string | null
  profileUrl?: string | null
  analytics?: ScreenInteractionContext
}

function formatPhoneField(value: string | undefined): { display: string; href: string } | null {
  if (!value?.trim()) return null
  return { display: formatPhoneDisplay(value), href: phoneTelHref(value) }
}

function externalUrl(raw: string): string {
  return /^https?:\/\//i.test(raw) || /^mailto:/i.test(raw)
    ? raw
    : `https://${raw.replace(/^\/*/, '')}`
}

export function ContactCardView({ contact, operatorId, profileUrl, analytics }: Props) {
  const emergency = formatPhoneField(contact.emergencyNumber ?? contact.hourEmergency)
  const office = formatPhoneField(contact.officePhone)
  const cell = formatPhoneField(contact.cellPhone)
  const backupEmergency = formatPhoneField(contact.emergencyPhoneNumber)
  const contactName = contact.displayName

  const hasAddress =
    !!contact.addressLine1?.trim() &&
    !!contact.city?.trim() &&
    !!contact.state?.trim()

  const trackPhone = (phone: string) => {
    if (!analytics) return
    trackPhonePressed({ ...analytics, phone, contact: contactName })
  }

  return (
    <article className="contact-card">
      {operatorId && contact.id && contact.fileExt ? (
        <OperatorInlineImage
          operatorId={operatorId}
          fileId={contact.id}
          fileExt={contact.fileExt}
          alt={contact.displayName}
          className="contact-card-photo"
        />
      ) : null}
      <h3 className="contact-card-name">
        {profileUrl ? (
          <Link to={profileUrl} className="contact-card-name-link">
            {contact.displayName}
          </Link>
        ) : (
          contact.displayName
        )}
      </h3>
      {contact.title ? <p className="contact-card-meta">{contact.title}</p> : null}
      {contact.contactType ? <p className="contact-card-type">{contact.contactType}</p> : null}
      {emergency ? (
        <p className="contact-card-line contact-card-line-emergency">
          <span className="contact-card-label">24hr Emergency</span>{' '}
          <a href={emergency.href} onClick={() => trackPhone(contact.emergencyNumber ?? contact.hourEmergency ?? '')}>
            {emergency.display}
          </a>
        </p>
      ) : null}
      {office ? (
        <p className="contact-card-line">
          <span className="contact-card-label">Office</span>{' '}
          <a href={office.href} onClick={() => trackPhone(contact.officePhone ?? '')}>
            {office.display}
          </a>
        </p>
      ) : null}
      {cell ? (
        <p className="contact-card-line">
          <span className="contact-card-label">Cell</span>{' '}
          <a href={cell.href} onClick={() => trackPhone(contact.cellPhone ?? '')}>
            {cell.display}
          </a>
        </p>
      ) : null}
      {!emergency && backupEmergency ? (
        <p className="contact-card-line">
          <span className="contact-card-label">Emergency</span>{' '}
          <a
            href={backupEmergency.href}
            onClick={() => trackPhone(contact.emergencyPhoneNumber ?? '')}
          >
            {backupEmergency.display}
          </a>
        </p>
      ) : null}
      {contact.email ? (
        <p className="contact-card-line">
          <span className="contact-card-label">Email</span>{' '}
          <a
            href={`mailto:${contact.email}`}
            onClick={
              analytics
                ? () =>
                    trackEmailPressed({
                      ...analytics,
                      email: contact.email!,
                      contact: contactName,
                    })
                : undefined
            }
          >
            {contact.email}
          </a>
        </p>
      ) : null}
      {hasAddress ? (
        <div className="contact-card-line contact-card-address">
          <span className="contact-card-label">Address</span>
          <div className="contact-card-address-lines">
            <div>{contact.addressLine1}</div>
            {contact.addressLine2 ? <div>{contact.addressLine2}</div> : null}
            <div>
              {[contact.city, contact.state].filter(Boolean).join(', ')}
              {contact.zip ? ` ${contact.zip}` : ''}
            </div>
          </div>
        </div>
      ) : null}
      {contact.url ? (
        <p className="contact-card-line">
          <a
            href={externalUrl(contact.url)}
            target="_blank"
            rel="noreferrer"
            onClick={
              analytics
                ? () =>
                    trackUrlPressed({
                      ...analytics,
                      url: contact.url!,
                      title: contact.url,
                    })
                : undefined
            }
          >
            {contact.url}
          </a>
        </p>
      ) : null}
    </article>
  )
}
